import { useState } from 'react'

import { Box, useClassNames } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Ai, Delete } from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles.css'
import {
  Column,
  ColumnsType,
} from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import {
  COVERAGE_SECTION_HEIGHT,
  COVERAGE_SECTION_MAX_HEIGHT,
} from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
import type { View } from 'modules/shift_management/lib/types'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import { stylesRefs } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index.styles'
import { AutoScheduleModal } from './AutoScheduleModal'
import CoverageShiftsCell from './CoverageShiftsCell'
import { DeleteCoverageModal } from './DeleteCoverageModal'
import { GenerateCoverageModal } from './GenerateCoverage'

interface CoverageSectionProps {
  columns: ColumnsType
  view: View
  setIsSubscribed: (isSubscribed: boolean) => void
  automationInProgress?: boolean
  resizeHeight: number
}

const Body = ({
  columns,
  view,
  setIsSubscribed,
  automationInProgress = false,
  resizeHeight,
}: CoverageSectionProps) => {
  const [showAutoScheduleModal, setShowAutoScheduleModal] = useState<boolean>(false)
  const [showDeleteCoverageModal, setShowDeleteCoverageModal] = useState<boolean>(false)
  const [showGenerateCoverageModal, setShowGenerateCoverageModal] = useState<boolean>(false)
  const cx = useClassNames(stylesRefs)
  const cells = columns.slice(1)
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  const [{ hasFeatureEntitlement }] = useEnabledFeatures()
  const width = dimensions[columnMinWidth]
  const [unassignedShifts] = useUnassignedShifts()
  const defaultShiftIds = unassignedShifts.reduce((acc: string[], { defaultShiftId }) => {
    if (defaultShiftId) acc.push(defaultShiftId)
    return acc
  }, [])
  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })
  const isAutomationEnabled = hasFeatureEntitlement(Features.shift_management_automatic_planning)
  const isDisabled = isAutomationEnabled && automationInProgress

  return (
    <>
      {showAutoScheduleModal && isAutomationEnabled && (
        <AutoScheduleModal
          onClose={() => setShowAutoScheduleModal(false)}
          setIsSubscribed={setIsSubscribed}
        />
      )}
      {showDeleteCoverageModal && isAutomationEnabled && (
        <DeleteCoverageModal onClose={() => setShowDeleteCoverageModal(false)} view={view} />
      )}
      {isAutomationEnabled && showGenerateCoverageModal && (
        <GenerateCoverageModal
          onClose={() => setShowGenerateCoverageModal(false)}
          view={view}
          setShowAutoScheduleModal={setShowAutoScheduleModal}
        />
      )}
      <Box
        width="full"
        className={cx('gridHeader')}
        style={{
          opacity: isDisabled ? '0.3' : '1',
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        <Box
          background="grey100"
          className={cx('header', 'sticky')}
          border={{
            right: {
              width: 's1',
              style: 'solid',
              color: 'grey300',
            },
          }}
          padding={resizeHeight < COVERAGE_SECTION_HEIGHT ? 's0' : 's16'}
          flexDirection="column"
          style={{
            height: `${resizeHeight}px`,
            maxHeight: `${COVERAGE_SECTION_MAX_HEIGHT}px`,
            minHeight: 'none',
            transition: (() => {
              return resizeHeight === 0 ? 'height 1s ease-out' : undefined
            })(),
          }}
          gap="s16"
        >
          {isAutomationEnabled && (
            <Box flexDirection="column" gap="s8" alignItems="flexStart">
              <Box style={{ display: resizeHeight < COVERAGE_SECTION_HEIGHT ? 'none' : '' }}>
                <F0Button
                  icon={Ai}
                  label={i18n.t('shift_management.coverage_section.generate_coverage')}
                  onClick={() => {
                    track(trackingEvents.shiftManagement.generateCoverageStart)
                    setShowGenerateCoverageModal(true)
                  }}
                  size="md"
                  variant="outline"
                />
                <F0Button
                  icon={Delete}
                  label={i18n.t('shift_management.coverage_section.delete_coverage')}
                  onClick={() => {
                    track(trackingEvents.shiftManagement.deleteCoverageStart)
                    setShowDeleteCoverageModal(true)
                  }}
                  size="md"
                  variant="ghost"
                  disabled={unassignedShifts.length <= 0}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          className={cx('scrollable')}
          background="grey100"
          style={{
            height: `${resizeHeight}px`,
            gridTemplateColumns: `repeat(${view.dates.length}, minmax(${width}, 1fr))`,
            maxHeight: `${COVERAGE_SECTION_MAX_HEIGHT}px`,
            minHeight: 'none',
            transition: (() => {
              return resizeHeight === 0 ? 'height 1s ease-out' : undefined
            })(),
          }}
        >
          {cells.map((heading: Column, index: number) => (
            <CoverageShiftsCell
              date={heading.date}
              key={index}
              plannedBreaks={plannedBreaksCollection.active}
            />
          ))}
        </Box>
      </Box>
    </>
  )
}

export default Body
